import { useState, useEffect } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { collection, query, where, getDocs, onSnapshot, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserGroups } from '../lib/firestore/groups';
import { getEventsByHost, getUpcomingEvents } from '../lib/firestore/events';
import { 
  isEventChatAvailable, 
  isGroupChatAvailable,
  sortEventsBySoonest,
  sortGroupsByRecent 
} from '../lib/chat-utils';
import '../styles/ChatList.css';

const ChatList = ({ onSelectChat }) => {
  const { user } = useAuthState();
  const [activeTab, setActiveTab] = useState('events'); // 'events' or 'groups'
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});

  useEffect(() => {
    if (!user) return;
    
    const loadChatsAndRefresh = async () => {
      await loadChats();
    };
    
    loadChatsAndRefresh();
    
    // Listen for refresh events (when returning from a chat)
    const handleRefresh = () => {
      loadChatsAndRefresh();
    };
    window.addEventListener('chatListRefresh', handleRefresh);
    
    return () => {
      window.removeEventListener('chatListRefresh', handleRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadChats = async () => {
    if (!user) {
      console.log('🔴 [ChatList] No user, skipping load');
      return;
    }
    setLoading(true);

    try {
      console.log('🔵 [ChatList] Starting to load chats for user:', user.uid);
      
      // Load user's groups
      console.log('🔵 [ChatList] Loading user groups...');
      const userGroups = await getUserGroups(user.uid);
      console.log('🔵 [ChatList] Loaded user groups:', userGroups);
      console.log('🔵 [ChatList] Number of groups:', userGroups.length);
      
      // Filter groups with chat enabled
      console.log('🔵 [ChatList] Checking groupChat for each group:');
      userGroups.forEach(group => {
        const groupChatData = group.groupChat;
        const enabled = groupChatData?.enabled;
        const isAvailable = isGroupChatAvailable(group);
        console.log(`  - Group ${group.id} (${group.name}):`, {
          hasGroupChat: !!groupChatData,
          groupChatObject: groupChatData,
          groupChatEnabled: enabled,
          enabledType: typeof enabled,
          isAvailable: isAvailable,
          fullGroupData: group
        });
      });
      const groupsWithChat = userGroups.filter(group => isGroupChatAvailable(group));
      console.log('🔵 [ChatList] Groups with chat enabled:', groupsWithChat.length);
      console.log('🔵 [ChatList] Groups with chat:', groupsWithChat);
      setGroups(sortGroupsByRecent(groupsWithChat));

      // Load events user is hosting or attending
      console.log('🔵 [ChatList] Loading profile events...');
      const profileEvents = await getEventsByHost('profile', user.uid);
      console.log('🔵 [ChatList] Profile events:', profileEvents);
      let allEvents = [...profileEvents];

      // Load events from groups
      console.log('🔵 [ChatList] Loading events from groups...');
      for (const group of userGroups) {
        const groupEvents = await getEventsByHost('group', group.id);
        console.log(`🔵 [ChatList] Events for group ${group.id}:`, groupEvents);
        allEvents = [...allEvents, ...groupEvents];
      }
      console.log('🔵 [ChatList] All events:', allEvents);

      // Filter events with chat enabled and available
      console.log('🔵 [ChatList] Checking event chat availability:');
      allEvents.forEach(event => {
        const eventChat = event.groupChat;
        const enabled = eventChat?.enabled;
        const isAvailable = isEventChatAvailable(event);
        console.log(`  - Event ${event.id} (${event.title}):`, {
          hasGroupChat: !!eventChat,
          eventChatObject: eventChat,
          eventChatEnabled: enabled,
          enabledType: typeof enabled,
          isAvailable: isAvailable,
          startsAt: event.startsAt
        });
      });
      const eventsWithChat = allEvents.filter(event => 
        isEventChatAvailable(event)
      );
      console.log('🔵 [ChatList] Events with chat available:', eventsWithChat.length);
      console.log('🔵 [ChatList] Events with chat:', eventsWithChat);
      const sortedEvents = sortEventsBySoonest(eventsWithChat);
      setEvents(sortedEvents);

      // Load unread counts for events and groups
      console.log('🔵 [ChatList] Loading unread counts...');
      const unreadPromises = [
        ...sortedEvents.map(event => loadUnreadCount('event', event.id)),
        ...groupsWithChat.map(group => loadUnreadCount('group', group.id)),
      ];
      await Promise.all(unreadPromises);
      console.log('🔵 [ChatList] Finished loading chats. Final state:', {
        groupsCount: groupsWithChat.length,
        eventsCount: sortedEvents.length,
        unreadCounts: unreadCounts
      });

    } catch (error) {
      console.error('🔴 [ChatList] Error loading chats:', error);
      console.error('🔴 [ChatList] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async (type, id) => {
    if (!user) return;

    try {
      console.log(`🔵 [ChatList] Loading unread count for ${type} ${id}`);
      // Get last read timestamp
      // Fixed: Use groupChatLastRead (not groupChat/lastRead)
      const lastReadPath = type === 'event'
        ? ['events', id, 'groupChatLastRead', user.uid]
        : ['groups', id, 'groupChatLastRead', user.uid];
      
      const lastReadDoc = await getDoc(doc(db, ...lastReadPath));
      const lastReadTime = lastReadDoc.exists() 
        ? (lastReadDoc.data().timestamp?.toDate ? lastReadDoc.data().timestamp.toDate() : new Date(0))
        : new Date(0);
      console.log(`🔵 [ChatList] Last read time for ${type} ${id}:`, lastReadTime);

      // Count messages after last read
      const messagesPath = type === 'event'
        ? ['events', id, 'groupChatMessages']
        : ['groups', id, 'groupChatMessages'];

      const messagesRef = collection(db, ...messagesPath);
      const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(50));
      const snapshot = await getDocs(q);
      console.log(`🔵 [ChatList] Messages snapshot for ${type} ${id}:`, snapshot.size);

      let unread = 0;
      snapshot.forEach((doc) => {
        const data = doc.data();
        const messageTime = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(0);
        if (messageTime > lastReadTime && data.userId !== user.uid) {
          unread++;
        }
      });

      console.log(`🔵 [ChatList] Unread count for ${type} ${id}:`, unread);
      setUnreadCounts(prev => ({ ...prev, [`${type}_${id}`]: unread }));
    } catch (error) {
      console.error(`🔴 [ChatList] Error loading unread count for ${type} ${id}:`, error);
      console.error('🔴 [ChatList] Error details:', {
        message: error.message,
        code: error.code
      });
    }
  };

  const getEventAvatar = (event) => {
    // Use first letter of title or emoji
    if (event.title) {
      return event.title.charAt(0).toUpperCase();
    }
    return '🎉';
  };

  const getGroupAvatar = (group) => {
    // Use first letter of name or emoji
    if (group.name) {
      return group.name.charAt(0).toUpperCase();
    }
    return '📁';
  };

  const formatEventTime = (event) => {
    if (!event.startsAt) return '';
    const startTime = event.startsAt?.toDate ? event.startsAt.toDate() : new Date(event.startsAt);
    const now = new Date();
    const diff = startTime.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `in ${days}d`;
    } else if (hours > 0) {
      return `in ${hours}h`;
    } else {
      return 'now';
    }
  };

  if (loading) {
    return (
      <div className="chat-list-loading">
        <div className="loading-spinner">Loading chats...</div>
      </div>
    );
  }

  return (
    <div className="chat-list">
      {/* Tabs */}
      <div className="chat-list-tabs">
        <button
          className={`chat-list-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events ({events.length})
        </button>
        <button
          className={`chat-list-tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Groups ({groups.length})
        </button>
      </div>

      {/* Events List */}
      {activeTab === 'events' && (
        <div className="chat-list-content">
          {events.length === 0 ? (
            <div className="chat-list-empty">
              <p>No event chats available</p>
              <small>Event chats open 24 hours before the event starts</small>
            </div>
          ) : (
            events.map((event) => (
              <div
                key={event.id}
                className="chat-list-item"
                onClick={() => {
                  onSelectChat('event', event.id);
                  // Clear unread count for this chat
                  setUnreadCounts(prev => ({ ...prev, [`event_${event.id}`]: 0 }));
                }}
              >
                <div className="chat-list-item-avatar event-avatar">
                  {getEventAvatar(event)}
                </div>
                <div className="chat-list-item-content">
                  <div className="chat-list-item-name">
                    {event.title || 'Untitled Event'}
                    {unreadCounts[`event_${event.id}`] > 0 && (
                      <span className="chat-unread-badge">
                        {unreadCounts[`event_${event.id}`]}
                      </span>
                    )}
                  </div>
                  <div className="chat-list-item-meta">
                    {formatEventTime(event)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Groups List */}
      {activeTab === 'groups' && (
        <div className="chat-list-content">
          {groups.length === 0 ? (
            <div className="chat-list-empty">
              <p>No group chats available</p>
              <small>Enable group chat when creating a group</small>
            </div>
          ) : (
            groups.map((group) => (
              <div
                key={group.id}
                className="chat-list-item"
                onClick={() => {
                  onSelectChat('group', group.id);
                  // Clear unread count for this chat
                  setUnreadCounts(prev => ({ ...prev, [`group_${group.id}`]: 0 }));
                }}
              >
                <div className="chat-list-item-avatar group-avatar">
                  {group.image ? (
                    <img src={group.image} alt={group.name} />
                  ) : (
                    getGroupAvatar(group)
                  )}
                </div>
                <div className="chat-list-item-content">
                  <div className="chat-list-item-name">
                    {group.name || 'Unnamed Group'}
                    {unreadCounts[`group_${group.id}`] > 0 && (
                      <span className="chat-unread-badge">
                        {unreadCounts[`group_${group.id}`]}
                      </span>
                    )}
                  </div>
                  <div className="chat-list-item-meta">
                    {group.description || 'Group chat'}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ChatList;

